import { type FC, useState } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";

import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";

import {
  PhysicalAssetForm,
  EMPTY_PHYSICAL_ASSET_FORM,
  validatePhysicalAssetFields,
  physicalAssetToItemFields,
  type PhysicalAssetFormValues,
  type PhysicalAssetFieldErrors,
} from "./PhysicalAssetForm";

export const AddPhysicalAsset: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, addItem, isAddingItem } = useEstateItems(estateId);
  const hasExisting = filterByCategory(assets, "Valuables").length > 0;

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    PhysicalAssetFormValues,
    PhysicalAssetFieldErrors
  >(EMPTY_PHYSICAL_ASSET_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const errors = validatePhysicalAssetFields(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    if (!estateId) return;

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;

      if (form.file) {
        sourceDocumentId = await uploadDocumentWithAuth(form.file, estateId, "other");
      }

      const fields = physicalAssetToItemFields(form);
      await addItem({
        estate_id: estateId,
        category: "Valuables",
        classification: "asset",
        item_name: form.description.trim(),
        item_type: fields.item_type,
        balance: fields.balance,
        notes: fields.notes,
        source_document_id: sourceDocumentId,
      });

      goToScreen("add_personal_property_step", {
        ...answers,
        physical_assets_review: "has_items",
        add_physical_asset: "done",
      });
    } catch (err) {
      setError("Failed to add item. Please try again.");
      sentryUtils.captureError(err, "AddPhysicalAsset submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">
        {hasExisting ? "Add another valuable" : "Add valuable personal property"}
      </h2>
      <p className="mt-2 text-center text-secondary">
        Share what you know. We'll help you gather details later.
      </p>
      <div className="mt-8 w-full">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          error={error}
          disabled={isAddingItem}
        >
          <PhysicalAssetForm form={form} onChange={updateForm} fieldErrors={fieldErrors} />
        </AddItemLayout>
      </div>
    </div>
  );
};
